<script context="module">
	import { z } from "zod";
	export const name = "Opinie";
	export const CustomFields = z.object({
		show_summary: z.boolean().optional(),
		show_role: z.boolean().optional(),
	});
</script>

<script>
	import { getContext } from "svelte";
	import { initials, avatarUrl, authorRole } from "../../includes/content.js";
	import Kicker from "./Kicker.svelte";
	import Stamp from "./Stamp.svelte";

	export let data = {};
	export let id = undefined;
	export let type = undefined;
	export let indexInContainer = undefined;
	id || type || indexInContainer;

	const context = getContext("context");

	$: content = data && data.content;
	$: cf = (data && data.custom_fields) || {};
	$: showSummary = cf.show_summary !== false;
	$: showRole = cf.show_role !== false;
	$: author = content && content.authors && content.authors[0];
	$: avatar = avatarUrl(author, 64);
	$: role = showRole && author ? authorRole(author, ($context && $context.domain) || (content && content.domain)) : null;
</script>

{#if content}
	<article class="af-card af-item af-item--opinion">
		<div class="af-card__body">
			<Kicker {content} />
			<h3 class="af-card__title"><a href={content.url}>{content.title}</a></h3>
			{#if author}
				<div class="af-byline">
					<span class="af-avatar" aria-hidden="true">
						{#if avatar}
							<img src={avatar} alt="" width="32" height="32" loading="lazy" />
						{:else}
							{initials(author.name)}
						{/if}
					</span>
					<span class="af-byline__text">
						<span class="af-byline__name">{author.name}</span>
						{#if role}<span class="af-byline__role">{role}</span>{/if}
					</span>
				</div>
			{/if}
			{#if showSummary && content.summary}
				<p class="af-card__summary">{content.summary}</p>
			{/if}
			<Stamp {content} />
		</div>
	</article>
{/if}

<style lang="scss">
	@use "../../styles/mixins" as m;

	.af-card {
		@include m.af-card;
	}

	.af-card__body {
		display: flex;
		flex-direction: column;
		gap: var(--af-space-3);
		padding: var(--af-space-6);
		flex: 1 1 auto;
	}

	.af-card__title {
		font: 600 var(--af-text-h3) / var(--af-leading-tight) var(--af-font-serif);
		font-style: italic;
		color: var(--af-ink);
		text-wrap: balance;

		a {
			@include m.af-stretched-link;
			@include m.af-clamp(4);

			/* Romanian low-9 opening quote in accent */
			&::before {
				content: "\201E";
				color: var(--af-accent);
			}
		}
	}

	.af-byline {
		display: flex;
		align-items: center;
		gap: var(--af-space-3);
		margin-top: var(--af-space-2);
	}

	.af-avatar {
		flex: 0 0 auto;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		overflow: hidden;
		display: grid;
		place-items: center;
		background: var(--af-surface-sunk);
		color: var(--af-ink-muted);
		font: 600 var(--af-text-meta) / 1 var(--af-font-serif);

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.af-byline__text {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.af-byline__name {
		font: 600 var(--af-text-ui) / 1.3 var(--af-font-sans);
		color: var(--af-ink);
	}

	.af-byline__role {
		font-size: var(--af-text-meta);
		color: var(--af-ink-muted);
	}

	.af-card__summary {
		font-size: var(--af-text-body-sm);
		line-height: 1.5;
		color: var(--af-ink-muted);
		@include m.af-clamp(3);
	}

	.af-card :global(.af-item__stamp) {
		margin-top: auto;
		padding-top: var(--af-space-2);
	}
</style>
